import React, { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../hooks/useAuth';
import '../styles/admin-common.css';
import { formatDate, formatDateTime, formatExamCode } from '../utils/format';

interface Coupon {
  coupon_code: string;
  exam_type_id: string;
  name: string;
  description?: string;
  max_uses: number;
  current_uses?: number;
  expires_at?: string;
  is_active?: boolean;
}

interface CouponUsageLog {
  coupon_code: string;
  user_email: string;
  used_at: string;
}

interface ExamType {
  exam_type_id: string;
  name: string;
}

interface CouponFormState {
  couponCode?: string;
  coupon_code?: string;
  examTypeId?: string;
  exam_type_id?: string;
  name: string;
  description?: string;
  maxUses?: number;
  max_uses?: number;
  expiresAt?: string;
  expires_at?: string;
  isActive?: boolean;
  is_active?: boolean;
}

interface CouponManagementPageProps {
  initialTab?: 'coupons' | 'logs';
}

const COUPONS_PER_PAGE = 10;

const CouponManagementPage: React.FC<CouponManagementPageProps> = ({ initialTab = 'coupons' }) => {
  const { user } = useAuth();
  const [coupons, setCoupons] = useState<Coupon[]>([]);
  const [usageLogs, setUsageLogs] = useState<CouponUsageLog[]>([]);
  const [creatingCoupon, setCreatingCoupon] = useState<CouponFormState | null>(null);
  const [editingCoupon, setEditingCoupon] = useState<CouponFormState | null>(null);
  const [activeCouponTab, setActiveCouponTab] = useState<'coupons' | 'logs'>(initialTab);
  const [couponCurrentPage, setCouponCurrentPage] = useState(1);
  const [totalCoupons, setTotalCoupons] = useState(0);
  const [loading, setLoading] = useState(false);
  const [examTypes, setExamTypes] = useState<ExamType[]>([]);
  const [couponSearchCode, setCouponSearchCode] = useState('');
  const [couponSearchEmail, setCouponSearchEmail] = useState('');

  const totalPages = useMemo(() => Math.max(1, Math.ceil(totalCoupons / COUPONS_PER_PAGE)), [totalCoupons]);

  const apiCall = async (url: string, options: RequestInit = {}) => {
    const token = localStorage.getItem('authToken');
    return fetch(`${import.meta.env.VITE_API_BASE_URL}${url}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
        ...options.headers,
      },
    });
  };

  const loadCoupons = async () => {
    setLoading(true);
    try {
      const offset = (couponCurrentPage - 1) * COUPONS_PER_PAGE;
      const response = await apiCall(`/coupon/admin/list?limit=${COUPONS_PER_PAGE}&offset=${offset}`);
      if (response.ok) {
        const result = await response.json();
        setCoupons(result.data.coupons || []);
        setTotalCoupons(result.data.total || result.data.coupons?.length || 0);
      }
    } catch (error) {
      console.error('Failed to load coupons:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadCouponUsageLogs = async (couponCode = couponSearchCode, userEmail = couponSearchEmail) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (couponCode.trim()) params.set('couponCode', couponCode.trim());
      if (userEmail.trim()) params.set('userEmail', userEmail.trim());

      const query = params.toString();
      const response = await apiCall(`/coupon/admin/usage-logs${query ? `?${query}` : ''}`);
      if (response.ok) {
        const result = await response.json();
        setUsageLogs(result.data.logs || []);
      }
    } catch (error) {
      console.error('Failed to load coupon usage logs:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadExamTypes = async () => {
    try {
      const response = await apiCall('/admin/exam-types');
      if (response.ok) {
        const result = await response.json();
        setExamTypes(result.data || []);
      }
    } catch (error) {
      console.error('Failed to load exam types:', error);
    }
  };

  const createCoupon = async (couponData: CouponFormState) => {
    try {
      const response = await apiCall('/coupon/admin/create', {
        method: 'POST',
        body: JSON.stringify(couponData),
      });
      if (response.ok) {
        setCreatingCoupon(null);
        loadCoupons();
        alert('쿠폰이 생성되었습니다.');
      } else {
        const error = await response.json();
        alert(error.message || '쿠폰 생성에 실패했습니다.');
      }
    } catch (error) {
      console.error('Failed to create coupon:', error);
      alert('쿠폰 생성 중 오류가 발생했습니다.');
    }
  };

  const updateCoupon = async (couponData: CouponFormState) => {
    try {
      const couponCode = couponData.coupon_code || couponData.couponCode;
      const response = await apiCall(`/coupon/admin/${couponCode}`, {
        method: 'PUT',
        body: JSON.stringify(couponData),
      });
      if (response.ok) {
        setEditingCoupon(null);
        loadCoupons();
        alert('쿠폰이 수정되었습니다.');
      } else {
        const error = await response.json();
        alert(error.message || '쿠폰 수정에 실패했습니다.');
      }
    } catch (error) {
      console.error('Failed to update coupon:', error);
      alert('쿠폰 수정 중 오류가 발생했습니다.');
    }
  };

  const toggleCouponStatus = async (couponCode: string, isActive: boolean) => {
    try {
      const response = await apiCall(`/coupon/admin/${couponCode}/status`, {
        method: 'PUT',
        body: JSON.stringify({ isActive }),
      });
      if (response.ok) {
        loadCoupons();
      }
    } catch (error) {
      console.error('Failed to toggle coupon status:', error);
    }
  };

  const deleteCoupon = async (couponCode: string) => {
    if (!window.confirm('이 쿠폰을 삭제하시겠습니까?')) return;

    try {
      const response = await apiCall(`/coupon/admin/${couponCode}`, { method: 'DELETE' });
      if (response.ok) {
        loadCoupons();
        alert('쿠폰이 삭제되었습니다.');
      } else {
        alert('쿠폰 삭제에 실패했습니다.');
      }
    } catch (error) {
      console.error('Failed to delete coupon:', error);
      alert('쿠폰 삭제 중 오류가 발생했습니다.');
    }
  };

  const resetLogSearch = () => {
    setCouponSearchCode('');
    setCouponSearchEmail('');
    loadCouponUsageLogs('', '');
  };

  const openCreateModal = () => {
    setCreatingCoupon({
      couponCode: '',
      examTypeId: '',
      name: '',
      description: '',
      maxUses: -1,
      expiresAt: '',
      isActive: true,
    });
  };

  const validateCouponForCreate = () => {
    if (!creatingCoupon) return;
    if (!creatingCoupon.couponCode?.trim()) {
      alert('쿠폰 코드를 입력하세요.');
      return;
    }
    if (!creatingCoupon.name.trim()) {
      alert('쿠폰명을 입력하세요.');
      return;
    }
    if (!creatingCoupon.examTypeId) {
      alert('시험 유형을 선택하세요.');
      return;
    }
    createCoupon(creatingCoupon);
  };

  const validateCouponForUpdate = () => {
    if (!editingCoupon) return;
    if (!editingCoupon.name.trim()) {
      alert('쿠폰명을 입력하세요.');
      return;
    }
    updateCoupon(editingCoupon);
  };

  useEffect(() => {
    loadExamTypes();
  }, []);

  useEffect(() => {
    if (activeCouponTab === 'coupons') {
      loadCoupons();
    } else {
      loadCouponUsageLogs();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [couponCurrentPage, activeCouponTab]);

  if (!user?.is_admin) {
    return (
      <div className="admin-page">
        <div className="access-denied">
          <h2>접근 권한이 없습니다</h2>
          <p>관리자 권한이 필요한 페이지입니다.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-page coupon-management-page">
      <header className="admin-page-title">
        <span className="admin-page-title__eyebrow">관리자 워크스페이스</span>
        <h1>쿠폰 관리</h1>
        <p>시험 접근 쿠폰, 사용량, 등록 로그를 관리합니다.</p>
      </header>

      <section className="admin-section coupon-management-panel">
        <div className="coupon-admin-tabs">
          <button
            type="button"
            className={`coupon-admin-tab ${activeCouponTab === 'coupons' ? 'active' : ''}`}
            onClick={() => setActiveCouponTab('coupons')}
          >
            쿠폰 목록
          </button>
          <button
            type="button"
            className={`coupon-admin-tab ${activeCouponTab === 'logs' ? 'active' : ''}`}
            onClick={() => setActiveCouponTab('logs')}
          >
            사용 로그
          </button>
        </div>

        {activeCouponTab === 'coupons' && (
          <>
            <div className="admin-header coupon-management-panel__header">
              <div>
                <h2>쿠폰 목록</h2>
                <p>발급된 쿠폰의 시험 유형, 사용량, 상태를 확인합니다.</p>
              </div>
              <div className="coupon-management-controls">
                <button className="admin-btn primary" type="button" onClick={openCreateModal}>
                  쿠폰 생성
                </button>
              </div>
            </div>

            <div className="coupons-stats">
              <span>총 {totalCoupons}개 쿠폰</span>
              <span>페이지 {couponCurrentPage} / {totalPages}</span>
            </div>

            <div className="coupon-admin-table-wrap">
              <table className="admin-table coupon-admin-table">
                <thead>
                  <tr>
                    <th className="col-coupon-name">쿠폰명</th>
                    <th className="col-coupon-code">코드</th>
                    <th className="col-coupon-exam">시험 유형</th>
                    <th className="col-coupon-uses">사용량</th>
                    <th className="col-coupon-expiry">만료일</th>
                    <th className="col-coupon-status">상태</th>
                    <th className="col-coupon-action">작업</th>
                  </tr>
                </thead>
                <tbody>
                  {coupons.length === 0 && (
                    <tr>
                      <td colSpan={7} className="admin-empty-cell">
                        {loading ? '쿠폰을 불러오는 중입니다.' : '표시할 쿠폰이 없습니다.'}
                      </td>
                    </tr>
                  )}
                  {coupons.map(coupon => (
                    <tr key={coupon.coupon_code}>
                      <td className="col-coupon-name">
                        <div className="coupon-name-cell">
                          <strong>{coupon.name}</strong>
                          {coupon.description && <span>{coupon.description}</span>}
                        </div>
                      </td>
                      <td className="col-coupon-code">
                        <span className="admin-badge info coupon-code-badge">{coupon.coupon_code}</span>
                      </td>
                      <td className="col-coupon-exam">
                        <span className="admin-badge secondary coupon-exam-badge">{formatExamCode(coupon.exam_type_id)}</span>
                      </td>
                      <td className="col-coupon-uses">
                        <span className="coupon-usage-count">
                          {coupon.current_uses || 0}/{coupon.max_uses === -1 ? '무제한' : coupon.max_uses}
                        </span>
                      </td>
                      <td className="col-coupon-expiry">
                        {coupon.expires_at ? (
                          <span className="admin-text-sm">{formatDate(coupon.expires_at)}</span>
                        ) : (
                          <span className="admin-text-gray-500">없음</span>
                        )}
                      </td>
                      <td className="col-coupon-status">
                        <span className={`admin-badge ${coupon.is_active ? 'success' : 'secondary'}`}>
                          {coupon.is_active ? '활성' : '비활성'}
                        </span>
                      </td>
                      <td className="col-coupon-action">
                        <div className="admin-flex">
                          <button className="admin-btn sm primary" type="button" onClick={() => setEditingCoupon(coupon)}>
                            수정
                          </button>
                          <button
                            className={`admin-btn sm ${coupon.is_active ? 'secondary' : 'success'}`}
                            type="button"
                            onClick={() => toggleCouponStatus(coupon.coupon_code, !coupon.is_active)}
                          >
                            {coupon.is_active ? '비활성' : '활성'}
                          </button>
                          <button className="admin-btn sm danger" type="button" onClick={() => deleteCoupon(coupon.coupon_code)}>
                            삭제
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {totalCoupons > COUPONS_PER_PAGE && (
              <div className="admin-pagination">
                <button
                  className="admin-btn secondary"
                  type="button"
                  disabled={couponCurrentPage === 1}
                  onClick={() => setCouponCurrentPage(couponCurrentPage - 1)}
                >
                  이전
                </button>
                <span className="page-info">
                  {couponCurrentPage} / {totalPages} 페이지
                </span>
                <button
                  className="admin-btn secondary"
                  type="button"
                  disabled={couponCurrentPage >= totalPages}
                  onClick={() => setCouponCurrentPage(couponCurrentPage + 1)}
                >
                  다음
                </button>
              </div>
            )}
          </>
        )}

        {activeCouponTab === 'logs' && (
          <>
            <div className="admin-header coupon-management-panel__header">
              <div>
                <h2>사용 로그</h2>
                <p>쿠폰 코드와 사용자 이메일 기준으로 등록 이력을 확인합니다.</p>
              </div>
              <div className="coupon-management-controls">
                <div className="admin-form-inline coupon-management-controls__group">
                  <input
                    type="text"
                    className="admin-form-control sm"
                    placeholder="쿠폰 코드 검색"
                    value={couponSearchCode}
                    onChange={event => setCouponSearchCode(event.target.value)}
                    onKeyDown={event => event.key === 'Enter' && loadCouponUsageLogs()}
                  />
                  <input
                    type="email"
                    className="admin-form-control sm"
                    placeholder="이메일 검색"
                    value={couponSearchEmail}
                    onChange={event => setCouponSearchEmail(event.target.value)}
                    onKeyDown={event => event.key === 'Enter' && loadCouponUsageLogs()}
                  />
                  <button className="admin-btn secondary" type="button" onClick={() => loadCouponUsageLogs()}>
                    검색
                  </button>
                  <button className="admin-btn secondary" type="button" onClick={resetLogSearch}>
                    초기화
                  </button>
                </div>
              </div>
            </div>

            <div className="coupon-admin-table-wrap">
              <table className="admin-table coupon-log-table">
                <thead>
                  <tr>
                    <th>쿠폰 코드</th>
                    <th>사용자</th>
                    <th>사용 일시</th>
                  </tr>
                </thead>
                <tbody>
                  {usageLogs.length === 0 && (
                    <tr>
                      <td colSpan={3} className="admin-empty-cell">
                        {loading ? '사용 로그를 불러오는 중입니다.' : '표시할 사용 로그가 없습니다.'}
                      </td>
                    </tr>
                  )}
                  {usageLogs.map((log, index) => (
                    <tr key={`${log.coupon_code}-${log.user_email}-${log.used_at}-${index}`}>
                      <td>
                        <span className="admin-badge info coupon-code-badge">{log.coupon_code}</span>
                      </td>
                      <td>
                        <span className="admin-text-sm">{log.user_email}</span>
                      </td>
                      <td>
                        <span className="admin-text-sm">{formatDateTime(log.used_at)}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </section>

      {creatingCoupon && (
        <div className="coupon-modal-overlay">
          <div className="coupon-modal">
            <div className="coupon-modal__header">
              <h3>쿠폰 생성</h3>
              <button className="coupon-modal__close" type="button" onClick={() => setCreatingCoupon(null)}>
                닫기
              </button>
            </div>
            <div className="coupon-modal__body">
              <CouponEditor coupon={creatingCoupon} examTypes={examTypes} onChange={setCreatingCoupon} isEditing={false} />
            </div>
            <div className="coupon-modal__footer">
              <button className="admin-btn primary" type="button" onClick={validateCouponForCreate}>
                생성
              </button>
              <button className="admin-btn secondary" type="button" onClick={() => setCreatingCoupon(null)}>
                취소
              </button>
            </div>
          </div>
        </div>
      )}

      {editingCoupon && (
        <div className="coupon-modal-overlay">
          <div className="coupon-modal">
            <div className="coupon-modal__header">
              <h3>쿠폰 수정</h3>
              <button className="coupon-modal__close" type="button" onClick={() => setEditingCoupon(null)}>
                닫기
              </button>
            </div>
            <div className="coupon-modal__body">
              <CouponEditor coupon={editingCoupon} examTypes={examTypes} onChange={setEditingCoupon} isEditing />
            </div>
            <div className="coupon-modal__footer">
              <button className="admin-btn primary" type="button" onClick={validateCouponForUpdate}>
                저장
              </button>
              <button className="admin-btn secondary" type="button" onClick={() => setEditingCoupon(null)}>
                취소
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const CouponEditor: React.FC<{
  coupon: CouponFormState;
  examTypes: ExamType[];
  onChange: (coupon: CouponFormState) => void;
  isEditing: boolean;
}> = ({ coupon, examTypes, onChange, isEditing }) => {
  const updateCoupon = (updates: Partial<CouponFormState>) => {
    onChange({ ...coupon, ...updates });
  };

  const couponCode = coupon.couponCode || coupon.coupon_code || '';
  const examTypeId = coupon.examTypeId || coupon.exam_type_id || '';
  const maxUses = coupon.maxUses !== undefined ? coupon.maxUses : (coupon.max_uses ?? -1);
  const expiresAt = coupon.expiresAt || coupon.expires_at || '';
  const isActive = coupon.isActive !== undefined ? coupon.isActive : coupon.is_active !== false;

  return (
    <div className="coupon-editor">
      <div className="form-group">
        <label>쿠폰 코드</label>
        <input
          type="text"
          className="admin-form-control"
          value={couponCode}
          onChange={event => updateCoupon({ couponCode: event.target.value, coupon_code: event.target.value })}
          placeholder="예: AWS-SAP-C02-KRUG-202604"
          disabled={isEditing}
        />
        {isEditing && <small className="form-text">쿠폰 코드는 수정할 수 없습니다.</small>}
      </div>

      <div className="form-group">
        <label>쿠폰명</label>
        <input
          type="text"
          className="admin-form-control"
          value={coupon.name || ''}
          onChange={event => updateCoupon({ name: event.target.value })}
          placeholder="관리자가 식별할 이름"
        />
      </div>

      <div className="form-group">
        <label>설명</label>
        <textarea
          className="admin-form-control"
          rows={2}
          value={coupon.description || ''}
          onChange={event => updateCoupon({ description: event.target.value })}
          placeholder="쿠폰 발급 목적이나 대상"
        />
      </div>

      <div className="form-group">
        <label>시험 유형</label>
        <select
          className="admin-form-control"
          value={examTypeId}
          onChange={event => updateCoupon({ examTypeId: event.target.value, exam_type_id: event.target.value })}
          disabled={isEditing}
        >
          <option value="">시험 유형 선택</option>
          {examTypes.map(type => (
            <option key={type.exam_type_id} value={type.exam_type_id}>
              {type.name}
            </option>
          ))}
        </select>
      </div>

      <div className="form-group">
        <label>최대 사용 횟수</label>
        <input
          type="number"
          className="admin-form-control"
          value={maxUses}
          onChange={event => {
            const value = Number.parseInt(event.target.value, 10);
            updateCoupon({ maxUses: Number.isNaN(value) ? -1 : value, max_uses: Number.isNaN(value) ? -1 : value });
          }}
          placeholder="-1 입력 시 무제한"
        />
        <small className="form-text">-1을 입력하면 사용 횟수를 제한하지 않습니다.</small>
      </div>

      <div className="form-group">
        <label>만료일</label>
        <input
          type="datetime-local"
          className="admin-form-control"
          value={expiresAt}
          onChange={event => updateCoupon({ expiresAt: event.target.value, expires_at: event.target.value })}
        />
        <small className="form-text">비워두면 만료일이 없습니다.</small>
      </div>

      <label className="admin-checkbox-label">
        <input
          type="checkbox"
          checked={isActive}
          onChange={event => updateCoupon({ isActive: event.target.checked, is_active: event.target.checked })}
        />
        활성 상태
      </label>
    </div>
  );
};

export default CouponManagementPage;
